import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {Alert, Autocomplete, Button, Divider, IconButton, Stack, Switch} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {useMutation} from "react-query";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import {useTranslation} from "react-i18next";
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {
    MewsConfig,
    MewsCredentials,
    MewsDataProcessingRules,
    MewsTaxCodeMapping
} from "crqlar-react-common/dist/models/integrations/mews/mews.config";
import {
    FullSyncConfig,
    PeriodicSyncConfig,
    PollerConfig
} from "crqlar-react-common/dist/models/integrations/pollerConfig.model";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import {LoadingButton} from "@mui/lab";
import {MewsService} from "crqlar-react-common/dist/services/integrations/mews/mews.service";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {enqueueSnackbar} from "notistack";
import {isRequiredTextMissing} from "src/utils/requiredField.util";

interface Props {
    mewsConfigData: UseEntityData<MewsConfig>
}

const MewsConfigForm = ({mewsConfigData}: Props) => {

    const {t} = useTranslation()
    const sectors: Sector[] = useAllSectors()

    const readOnlyUser = useReadOnly()

    const mewsConfig = mewsConfigData.item
    const nameMissing = isRequiredTextMissing(mewsConfigData, mewsConfig.name)
    const setMewsConfig = mewsConfigData.setItem

    const ageCategories = useAllAgeCategories()

    //region Handlers

    const checkConnection = useMutation(
        ['checkMewsConfig', mewsConfig.credentials],
        async () => {
            let success = false
            try {
                if (mewsConfig.credentials) {
                    await MewsService.checkConnection(mewsConfig);
                    success = true
                }
            } catch (error) {
            }

            enqueueSnackbar(
                success ? t('Connection successful') : t('Connection failed'),
                {
                    variant: success ? 'success' : 'error',
                    anchorOrigin: {vertical: 'bottom', horizontal: 'center'},
                }
            )
        }
    )

    const updateField = (field: keyof MewsConfig, value: any) => {
        setMewsConfig({...mewsConfig, [field]: value})
    }

    const updateCredentialsField = (field: keyof MewsCredentials, value: any) => {
        setMewsConfig({
            ...mewsConfig,
            credentials: {
                ...mewsConfig.credentials,
                [field]: value
            }
        })
    }

    const updatePollerConfig = (field: keyof PollerConfig, value: any) => {
        setMewsConfig({
            ...mewsConfig,
            pollerConfig: {
                ...mewsConfig.pollerConfig,
                [field]: value
            }
        })
    }

    const updateFullSyncConfig = (field: keyof FullSyncConfig, value: any) => {
        updatePollerConfig('fullSync', {
            ...mewsConfig.pollerConfig?.fullSync,
            [field]: value
        })
    }

    const updateQuickConfig = (field: keyof PeriodicSyncConfig, value: any) => {
        updatePollerConfig('quickSync', {
            ...mewsConfig.pollerConfig?.quickSync,
            [field]: value
        })
    }

    const updateDataProcessingRules = (field: keyof MewsDataProcessingRules, value: any) => {
        updateField('dataProcessingRules', {
            ...mewsConfig.dataProcessingRules,
            [field]: value
        })
    }

    const taxCodeMappings: MewsTaxCodeMapping[] = mewsConfig.dataProcessingRules?.taxCodeMappings || []

    const updateTaxCodeMapping = (index: number, field: keyof MewsTaxCodeMapping, value: any) => {
        updateDataProcessingRules('taxCodeMappings', taxCodeMappings.map((m, i) => i === index ? {...m, [field]: value} : m))
    }

    //endregion Handlers

    //region UI

    const credentialsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Credentials')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Integration name')}
                    tooltip={t('Internal label for identifying this Mews integration configuration.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={mewsConfig.name || ''}
                        error={nameMissing}
                        fullWidth
                        onChange={e => updateField('name', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Client Token')}
                    tooltip={t('Mews client token used by this integration.')}
                >
                    <TextField
                        id={'client-token'}
                        size={'small'}
                        value={mewsConfig.credentials?.clientToken || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('clientToken', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Access Token')}
                    tooltip={t('Mews access token used to authorize API requests.')}
                >
                    <TextField
                        id={'access-token'}
                        size={'small'}
                        value={mewsConfig.credentials?.accessToken || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('accessToken', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Client Name')}
                    tooltip={t('Client name registered for this Mews integration.')}
                >
                    <TextField
                        id={'client-name'}
                        size={'small'}
                        value={mewsConfig.credentials?.clientName || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('clientName', e.target.value)}
                    />
                </Property>

                <Stack direction={'row'} justifyContent={'flex-start'}>
                    <LoadingButton
                        disabled={readOnlyUser}
                        loading={checkConnection.isLoading}
                        onClick={() => checkConnection.mutate()}
                        variant={'outlined'}
                    >
                        {t('Test connection')}
                    </LoadingButton>
                </Stack>
            </Stack>
        </Stack>
    )

    const pollerConfig = (
        <AdditionalProperties title={t('Data sync setup')} variant={'outlined'}>

            <Typography textAlign={'start'} sx={{mt: 1}}>
                {t('Full sync')}
            </Typography>

            <Property
                title={t('Profile created in past days')}
                tooltip={t('How far back full sync should look for profiles created in Mews.')}
            >
                <TextField
                    id={'profile-days-past'}
                    size={'small'}
                    value={mewsConfig.pollerConfig?.fullSync?.profileDaysPast || ''}
                    onChange={(e) => updateFullSyncConfig('profileDaysPast', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

            <Property
                title={t('Reservation created in past days')}
                tooltip={t('How far back full sync should look for reservations created in Mews.')}
            >
                <TextField
                    id={'reservation-days-past'}
                    size={'small'}
                    value={mewsConfig.pollerConfig?.fullSync?.reservationDaysPast || ''}
                    onChange={(e) => updateFullSyncConfig('reservationDaysPast', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

            <Property
                title={t('Stale timeout minutes')}
                tooltip={t('Minutes before a full sync job is treated as stale.')}
            >
                <TextField
                    id={'stale-timeout-minutes'}
                    size={'small'}
                    value={mewsConfig.pollerConfig?.fullSync?.staleTimeoutMinutes || ''}
                    onChange={(e) => updateFullSyncConfig('staleTimeoutMinutes', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

            <Typography textAlign={'start'} sx={{mt: 2}}>
                {t('Quick sync')}
            </Typography>

            <Property
                title={t('Enabled')}
                tooltip={t('Turns scheduled quick sync on or off.')}
            >
                <Switch
                    id={'quick-sync-scheduled'}
                    checked={mewsConfig.pollerConfig?.quickSync?.enabled || false}
                    onChange={(e) => updateQuickConfig('enabled', e.target.checked)}
                />
            </Property>

            <Property
                title={t('Stale timeout minutes')}
                tooltip={t('Minutes before a quick sync job is treated as stale.')}
            >
                <TextField
                    id={'quick-stale-timeout-minutes'}
                    size={'small'}
                    value={mewsConfig.pollerConfig?.quickSync?.staleTimeoutMinutes || ''}
                    onChange={(e) => updateQuickConfig('staleTimeoutMinutes', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

            <Property
                title={t('Sync period minutes')}
                tooltip={t('Minutes between scheduled quick sync runs.')}
            >
                <TextField
                    id={'quick-sync-period-minutes'}
                    size={'small'}
                    value={mewsConfig.pollerConfig?.quickSync?.syncPeriodMinutes || ''}
                    onChange={(e) => updateQuickConfig('syncPeriodMinutes', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

        </AdditionalProperties>
    )

    const dataProcessingRules = (
        <AdditionalProperties title={t('Data processing rules')} variant={'outlined'}>

            <Property
                title={t('Allow charge posting')}
                tooltip={t('Allows Crqlar to post charges back to Mews when supported.')}
            >
                <Switch
                    id={'allow-charge'}
                    checked={mewsConfig.dataProcessingRules?.allowCharge || false}
                    onChange={(e) => updateDataProcessingRules('allowCharge', e.target.checked)}
                />
            </Property>

            <Property
                title={t('Accommodation sector')}
                tooltip={t('Sector where imported accommodation reservations and resources should be mapped.')}
            >
                <Autocomplete
                    id={'sectors'}
                    options={sectors}
                    getOptionLabel={(option: any) => option.name || ''}
                    value={sectors.find(s => s.id === mewsConfig.dataProcessingRules?.accommodationSectorId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('accommodationSectorId', value?.id)
                    }}
                />
            </Property>

            <Property
                title={t('Default sector')}
                tooltip={t('Fallback sector used when imported Mews data does not match a more specific sector.')}
            >
                <Autocomplete
                    id={'sectors'}
                    options={sectors}
                    getOptionLabel={(option: any) => option.name || ''}
                    value={sectors.find(s => s.id === mewsConfig.dataProcessingRules?.defaultSectorId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('defaultSectorId', value?.id)
                    }}
                />
            </Property>

            <Property
                title={t('Default billing services (MEWS ID)')}
                tooltip={t('Mews service ID used as the default billing service for imported charges.')}
            >
                <TextField
                    id={'default-billing-service-id'}
                    size={'small'}
                    value={mewsConfig.dataProcessingRules?.defaultBillingServiceId || ''}
                    onChange={(e) => updateDataProcessingRules('defaultBillingServiceId', e.target.value)}
                    fullWidth
                />
            </Property>

            <Property
                title={t('VAT rate to Mews tax code')}
                tooltip={t('Mews tax code sent with each posted charge, per Crqlar VAT rate (e.g. 8.1 -> the Swiss standard rate code from Mews). Charges with an unmapped rate are posted without VAT.')}
            >
                <Stack gap={1} width={'100%'}>
                    {taxCodeMappings.map((mapping, index) => (
                        <Stack key={index} direction={'row'} gap={1} alignItems={'center'}>
                            <TextField
                                id={`tax-code-rate-${index}`}
                                size={'small'}
                                type={'number'}
                                label={t('VAT %')}
                                value={mapping.rate ?? ''}
                                onChange={(e) => updateTaxCodeMapping(index, 'rate', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                                sx={{width: 120}}
                            />
                            <TextField
                                id={`tax-code-${index}`}
                                size={'small'}
                                label={t('Mews tax code')}
                                value={mapping.taxCode || ''}
                                onChange={(e) => updateTaxCodeMapping(index, 'taxCode', e.target.value)}
                                fullWidth
                            />
                            <IconButton
                                disabled={readOnlyUser}
                                onClick={() => updateDataProcessingRules('taxCodeMappings', taxCodeMappings.filter((_, i) => i !== index))}
                            >
                                <DeleteOutlineIcon/>
                            </IconButton>
                        </Stack>
                    ))}
                    <Stack direction={'row'} justifyContent={'flex-start'}>
                        <Button
                            disabled={readOnlyUser}
                            startIcon={<AddIcon/>}
                            onClick={() => updateDataProcessingRules('taxCodeMappings', [...taxCodeMappings, {}])}
                        >
                            {t('Add tax code')}
                        </Button>
                    </Stack>
                </Stack>
            </Property>

        </AdditionalProperties>
    )

    const configurationSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Configuration')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Property ID / Name (optional, multiproperty)')}
                    tooltip={t('Optional property identifier assigned to all data imported through this connector.')}
                >
                    <Stack
                        width={'100%'}
                        gap={1}
                    >
                        <Alert
                            variant={'outlined'}
                            severity={'info'}
                            sx={{py: 0, lineHeight: 1, alignItems: 'center'}}
                        >
                            {t('Put property id or name here. If set, all incoming data via this connector (profiles, reservations...) will have this property id. You will be able to filter data by this property id in the future')}
                        </Alert>

                        <Alert
                            variant={'outlined'}
                            severity={'error'}
                            sx={{py: 0, lineHeight: 1, alignItems: 'center'}}
                        >
                            {t('Do not change this value if you are not sure what you are doing! It may cause data inconsistency. If you want to change it, please first delete all data from this connector')}
                        </Alert>

                        <TextField
                            id={'property-id'}
                            size={'small'}
                            value={mewsConfig.propertyId || ''}
                            onChange={(e) => updateField('propertyId', e.target.value)}
                            fullWidth
                            required
                        />
                    </Stack>
                </Property>

                {pollerConfig}
                {dataProcessingRules}
            </Stack>
        </Stack>
    )

    //endregion UI

    return (
        <Stack
            sx={{
                width: '100%',
                gap: 6,
            }}
        >
            {credentialsSection}
            <Divider/>
            {configurationSection}
        </Stack>
    )

}

export default MewsConfigForm;
